import type { ReferenceSourcesResponse } from "../../shared/referenceSources";
import type { DischargeMeta, DischargeFilters, DischargeResponse, CoordinationResponse } from "../../shared/discharge";
// src/lib/api.ts (v2)
// fetch ラッパ。shared/api.ts の型をそのまま使う。
import type {
  SokuhouUriageResponse, SokuhouRpFilters, SokuhouRpResponse,
  MetaResponse,
  QueryFilters, MonthlyResponse, RankingResponse,
  SummaryFilters, SummaryResponse,
  KpiFilters, KpiResponse,
  HeatmapFilters, HeatmapResponse,
  AlertsFilters, AlertsResponse,
  PvmFilters, PvmResponse,
  PortfolioFilters, PortfolioResponse,
  DetailFilters, DetailResponse,
  SurgeryFilters, SurgeryResponse,
  KbRevenueFilters, KbRevenueResponse,
  MeisaiMoversFilters, MeisaiMoversResponse,
  RouteFilters, RouteResponse,
  PlResponse,
  DeptMetricsAllFilters, DeptMetricsAllResponse,
  DeptMatrixFilters, DeptMatrixResponse,
  DoctorMatrixFilters, DoctorMatrixResponse,
  PresetsResponse,
  ProfitFilters, ProfitResponse,
  KbActFilters, KbActResponse,
  InpatientFeeAnalysisFilters, InpatientFeeAnalysisResponse,
  ShortStay3AnalysisFilters, ShortStay3AnalysisResponse,
  RoomChargeResponse,
  Kyusou3PaceResponse,
  SurgeryKaikeiResponse,
  BillingReconResponse,
  BillingCompareResponse,
  SurgeryRpFilters, SurgeryRpResponse,
  NursingAcuityResponse,
} from "@shared/api";
import { GP_MODE_HEADER } from "./gpMode";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    ...init,
    // [正式版GP 2026-08-25] X-GP-Mode: jinken を **全 API 呼び出しに**付ける。
    //   server はこのヘッダで「医師人件費控除後GP」のビューに切り替える (別 DB は無い)。
    //   ★ここ 1 箇所に集約する。個別 endpoint に付けて回ると必ず付け忘れが出る。
    headers: { "Content-Type": "application/json", ...GP_MODE_HEADER, ...(init?.headers ?? {}) },
  });
  if (res.status === 401) {
    // 未認証 → ログイン画面へ
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.assign("/login?next=" + encodeURIComponent(window.location.pathname));
    }
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText} — ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  referenceSources: () => jsonFetch<ReferenceSourcesResponse>("/api/reference-sources"),
  dischargeMeta: () => jsonFetch<DischargeMeta>("/api/discharge/meta"),
  discharge: (f: DischargeFilters) => jsonFetch<DischargeResponse>("/api/discharge/query", {method:"POST", body:JSON.stringify(f)}),
  dischargeCoordination: (f: DischargeFilters) => jsonFetch<CoordinationResponse>("/api/discharge/coordination", {method:"POST", body:JSON.stringify(f)}),
  // GP新定義の関連指標は 2026-08-25 に正式昇格済み。server の一覧をそのまま使う。
  meta: (): Promise<MetaResponse> => jsonFetch<MetaResponse>("/api/meta"),

  monthly: (f: QueryFilters): Promise<MonthlyResponse> =>
    jsonFetch<MonthlyResponse>("/api/query/monthly", {
      method: "POST", body: JSON.stringify(f),
    }),

  ranking: (f: QueryFilters): Promise<RankingResponse> =>
    jsonFetch<RankingResponse>("/api/query/ranking", {
      method: "POST", body: JSON.stringify(f),
    }),

  summary: (f: SummaryFilters): Promise<SummaryResponse> =>
    jsonFetch<SummaryResponse>("/api/query/summary", {
      method: "POST", body: JSON.stringify(f),
    }),

  kpi: (f: KpiFilters): Promise<KpiResponse> =>
    jsonFetch<KpiResponse>("/api/query/kpi", {
      method: "POST", body: JSON.stringify(f),
    }),

  heatmap: (f: HeatmapFilters): Promise<HeatmapResponse> =>
    jsonFetch<HeatmapResponse>("/api/query/heatmap", {
      method: "POST", body: JSON.stringify(f),
    }),

  alerts: (f: AlertsFilters): Promise<AlertsResponse> =>
    jsonFetch<AlertsResponse>("/api/query/alerts", {
      method: "POST", body: JSON.stringify(f),
    }),

  pvm: (f: PvmFilters): Promise<PvmResponse> =>
    jsonFetch<PvmResponse>("/api/query/pvm", {
      method: "POST", body: JSON.stringify(f),
    }),

  profit: (f: ProfitFilters): Promise<ProfitResponse> =>
    jsonFetch<ProfitResponse>("/api/query/profit", {
      method: "POST", body: JSON.stringify(f),
    }),

  kbAct: (f: KbActFilters): Promise<KbActResponse> =>
    jsonFetch<KbActResponse>("/api/query/kb_act", {
      method: "POST", body: JSON.stringify(f),
    }),

  inpatientFeeAnalysis: (f: InpatientFeeAnalysisFilters): Promise<InpatientFeeAnalysisResponse> =>
    jsonFetch<InpatientFeeAnalysisResponse>("/api/query/inpatient_fee_analysis", {
      method: "POST", body: JSON.stringify(f),
    }),

  shortStay3Analysis: (f: ShortStay3AnalysisFilters): Promise<ShortStay3AnalysisResponse> =>
    jsonFetch<ShortStay3AnalysisResponse>("/api/query/short_stay3_analysis", {
      method: "POST", body: JSON.stringify(f),
    }),

  roomCharge: (): Promise<RoomChargeResponse> =>
    jsonFetch<RoomChargeResponse>("/api/query/room_charge", {
      method: "POST", body: JSON.stringify({}),
    }),

  kyusou3Pace: (): Promise<Kyusou3PaceResponse> =>
    jsonFetch<Kyusou3PaceResponse>("/api/query/kyusou3_pace", {
      method: "POST", body: JSON.stringify({}),
    }),

  surgeryRp: (f: SurgeryRpFilters): Promise<SurgeryRpResponse> =>
    jsonFetch<SurgeryRpResponse>("/api/query/surgery_rp", {
      method: "POST", body: JSON.stringify(f),
    }),

  surgeryKaikei: (): Promise<SurgeryKaikeiResponse> =>
    jsonFetch<SurgeryKaikeiResponse>("/api/query/surgery_kaikei", {
      method: "POST", body: JSON.stringify({}),
    }),

  billingRecon: (): Promise<BillingReconResponse> =>
    jsonFetch<BillingReconResponse>("/api/query/billing_recon", {
      method: "POST", body: JSON.stringify({}),
    }),

  billingCompare: (): Promise<BillingCompareResponse> =>
    jsonFetch<BillingCompareResponse>("/api/query/billing_compare", {
      method: "POST", body: JSON.stringify({}),
    }),

  sokuhouUriage: (): Promise<SokuhouUriageResponse> =>
    jsonFetch<SokuhouUriageResponse>("/api/query/sokuhou_uriage", {
      method: "POST", body: JSON.stringify({}),
    }),

  sokuhouUriageRp: (f: SokuhouRpFilters): Promise<SokuhouRpResponse> =>
    jsonFetch<SokuhouRpResponse>("/api/query/sokuhou_uriage_rp", {
      method: "POST", body: JSON.stringify(f),
    }),

  nursingAcuity: (): Promise<NursingAcuityResponse> =>
    jsonFetch<NursingAcuityResponse>("/api/query/nursing_acuity", {
      method: "POST", body: JSON.stringify({}),
    }),

  portfolio: (f: PortfolioFilters): Promise<PortfolioResponse> =>
    jsonFetch<PortfolioResponse>("/api/query/portfolio", {
      method: "POST", body: JSON.stringify(f),
    }),

  detail: (f: DetailFilters): Promise<DetailResponse> =>
    jsonFetch<DetailResponse>("/api/query/detail", {
      method: "POST", body: JSON.stringify(f),
    }),

  surgery: (f: SurgeryFilters): Promise<SurgeryResponse> =>
    jsonFetch<SurgeryResponse>("/api/query/surgery", {
      method: "POST", body: JSON.stringify(f),
    }),

  kbRevenue: (f: KbRevenueFilters): Promise<KbRevenueResponse> =>
    jsonFetch<KbRevenueResponse>("/api/query/kb_revenue", {
      method: "POST", body: JSON.stringify(f),
    }),
  meisaiMovers: (f: MeisaiMoversFilters): Promise<MeisaiMoversResponse> =>
    jsonFetch<MeisaiMoversResponse>("/api/query/meisai_movers", {
      method: "POST", body: JSON.stringify(f),
    }),

  route: (f: RouteFilters): Promise<RouteResponse> =>
    jsonFetch<RouteResponse>("/api/query/route", {
      method: "POST", body: JSON.stringify(f),
    }),

  pl: (): Promise<PlResponse> =>
    jsonFetch<PlResponse>("/api/query/pl"),

  deptMetricsAll: (f: DeptMetricsAllFilters): Promise<DeptMetricsAllResponse> =>
    jsonFetch<DeptMetricsAllResponse>("/api/query/dept_metrics_all", {
      method: "POST", body: JSON.stringify(f),
    }),

  deptMatrix: (f: DeptMatrixFilters): Promise<DeptMatrixResponse> =>
    jsonFetch<DeptMatrixResponse>("/api/query/dept_matrix", {
      method: "POST", body: JSON.stringify(f),
    }),

  // [2026-08-03] 入院実績(医師別) タブ: 医師 × 在院診療科 × 指標 × FY12ヶ月
  doctorMatrix: (f: DoctorMatrixFilters): Promise<DoctorMatrixResponse> =>
    jsonFetch<DoctorMatrixResponse>("/api/query/doctor_matrix", {
      method: "POST", body: JSON.stringify(f),
    }),

  // 条件セット (presets)
  presetsList: (feature: string): Promise<PresetsResponse> =>
    jsonFetch<PresetsResponse>(`/api/presets?feature=${encodeURIComponent(feature)}`),
  presetCreate: (f: { feature: string; name: string; config: unknown; shared?: boolean }):
    Promise<{ ok: boolean; id: number; isShared: boolean }> =>
    jsonFetch("/api/presets", { method: "POST", body: JSON.stringify(f) }),
  presetDelete: (id: number): Promise<{ ok: boolean }> =>
    jsonFetch(`/api/presets/${id}`, { method: "DELETE" }),
};
